import { useDeviceDrawerData } from '../../hooks/useDeviceDrawerData'
import { getFloorPlanHref, useNavigation } from '@/hooks/useNavigation'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import {
  ArrowLeftIcon,
  type BreadcrumbItem,
  CameraIcon,
  type ColorScheme,
  DeviceStatusIcon,
  DoorIcon,
  EditIcon,
  Flex,
  HStack,
  LocationBreadcrumb,
  Skeleton,
  UtilityButton,
  tokenVar,
} from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { getDeviceStatusIconStatus } from '../utils/getDeviceStatusIconStatus'
import { crumbStyles } from './DarkDeviceDrawerHeader.styles'

type DeviceDrawerData = ReturnType<typeof useDeviceDrawerData>

interface IProps {
  device: DeviceDrawerData['device']
  deviceType: DeviceType
  isLoading: boolean
  canEdit: boolean
  colorScheme: ColorScheme
  onClose: () => void
  /** Opens the edit-device modal. The caller builds the device payload. */
  onEdit: () => void
}

/**
 * Header chrome for the reskinned device drawer (ENG-32123): back / edit
 * `UtilityButton`s, the device-type icon + health status, and the
 * device/floor/facility `LocationBreadcrumb`.
 */
export const DarkDeviceDrawerHeader = ({
  device,
  deviceType,
  isLoading,
  canEdit,
  colorScheme,
  onClose,
  onEdit,
}: IProps) => {
  const { t } = useTranslation()
  const { goToFloorPlan } = useNavigation()
  const { showSuccess } = useToasts({ isRebrand: true, colorScheme })

  const prefix = colorScheme === 'light' ? 'light.' : ''
  const iconColor = tokenVar(
    colorScheme === 'light'
      ? 'colors.light.icon.secondary'
      : 'colors.icon.secondary'
  )
  // Back/edit buttons sit flush on the drawer's card surface rather than the
  // default UtilityButton card-elevated background.
  const buttonBg = colorScheme === 'light' ? 'light.bg.card' : 'bg.card'

  const isCamera = deviceType === DeviceType.Camera
  const editLabel = isCamera
    ? t('devices:drawer_edit_camera_tooltip')
    : t('devices:drawer_edit_door_tooltip')
  const editTooltip = canEdit
    ? editLabel
    : t('devices:drawer_no_permissions_tooltip')

  const floorId = device?.floor?.id
  // Only wire floor-plan navigation when there's a real floor to open —
  // otherwise the crumb stays copy-only rather than dispatching the floor
  // drawer with an empty selection.
  const navigateToFloor = floorId
    ? () => {
        goToFloorPlan(floorId)
        onClose()
      }
    : undefined
  const floorHref = getFloorPlanHref(floorId)

  // Crumbs are omitted entirely when their label is missing (no floor / no
  // facility) rather than rendering an empty, clickable crumb.
  const breadcrumbItems: BreadcrumbItem[] = [
    ...(device?.name
      ? // The device name is click-to-copy, matching the legacy breadcrumb.
        [{ label: device.name, copyable: true }]
      : []),
    ...(device?.floor?.name
      ? [
          {
            label: tVar(t, 'devices:drawer_floor_breadcrumb', {
              name: device.floor.name,
            }),
            href: floorHref,
            onClick: navigateToFloor,
            copyable: true,
          },
        ]
      : []),
    ...(device?.facility?.shortName
      ? [
          {
            label: device.facility.shortName,
            href: floorHref,
            onClick: navigateToFloor,
            copyable: true,
          },
        ]
      : []),
  ]

  return (
    <Flex
      alignItems='center'
      borderBottomWidth='1px'
      borderColor={`${prefix}border.subtle`}
      borderStyle='solid'
      h='64px'
      justifyContent='space-between'
      px='16px'
      w='full'
    >
      <HStack gap='8px' minW={0}>
        <UtilityButton
          colorScheme={colorScheme}
          icon={<ArrowLeftIcon />}
          label={t('common:back')}
          onClick={onClose}
          stylesOverride={{ bg: buttonBg, border: 'none' }}
          tooltipLabel={t('common:back')}
        />
        {/* Device-type icon + health status. Composition pending final design
            confirmation (see ENG-32123 fidelity notes). */}
        {isLoading ? (
          <Skeleton borderRadius='full' h='24px' w='24px' />
        ) : (
          <HStack gap='6px'>
            {isCamera ? (
              <CameraIcon color={iconColor} size={18} />
            ) : (
              <DoorIcon color={iconColor} size={18} />
            )}
            <DeviceStatusIcon
              colorScheme={colorScheme}
              size={12}
              status={getDeviceStatusIconStatus(device?.status)}
            />
          </HStack>
        )}
        {isLoading ? (
          <Skeleton h='16px' w='220px' />
        ) : (
          <LocationBreadcrumb
            colorScheme={colorScheme}
            containerStyleOverrides={{ px: '0', py: '0' }}
            copiedLabel={t('common:copied')}
            itemStyleOverrides={crumbStyles}
            items={breadcrumbItems}
            onCopied={() => showSuccess(t('common:copied'))}
          />
        )}
      </HStack>
      <UtilityButton
        colorScheme={colorScheme}
        disabled={!canEdit}
        icon={<EditIcon />}
        label={editLabel}
        onClick={onEdit}
        stylesOverride={{ bg: buttonBg, border: 'none' }}
        tooltipLabel={editTooltip}
      />
    </Flex>
  )
}
